/**
 * IONLOOP Assistant — orchestrator.
 *
 * Wires the local retrieval engine (Phase 3), the deterministic answer
 * composer (Phase 4) and the conversation context layer (Phase 5) behind a
 * small API: initIonloopAssistant() / ask().
 *
 * Everything runs locally against the generated IONLOOP knowledge index.
 * No external AI, no network.
 */

var loader = require('./knowledge/knowledge-loader');
var search = require('./knowledge/knowledge-search');
var composer = require('./answer/answer-composer');
var conversationMod = require('./conversation/conversation-context');
var resolver = require('./conversation/context-resolver');
var graphMod = require('./conversation/entity-graph');
var renderer = require('./render-answer');
var uiBuilder = require('./assistant-ui');
var commandRouter = require('./command-router');
var categoriesMod = require('./knowledge/categories');

/**
 * Initialize the assistant: load the knowledge index, prepare the search
 * structure and the entity graph, create a fresh session conversation, AND
 * build the enterprise UI shell.
 * @returns {Promise<Object>} assistant instance
 */
function initIonloopAssistant(options) {
    options = options || {};

    return loader.loadKnowledgeIndex().then(function (index) {
        search.setKnowledgeIndex(index.documents);
        composer.resetCache();

        var conversation = conversationMod.createConversation();
        var graph = graphMod.buildGraph(index.documents);

        var ui = uiBuilder.buildAssistantUI({
            onSubmit: function (question) {
                return askAssistant(question, {}, {
                    conversation: conversation,
                    graph: graph,
                    index: index,
                    debug: !!(options.debug)
                });
            },
            onCommand: function (input) {
                // Slash commands are handled locally, before retrieval.
                return commandRouter.executeCommand(input, {
                    conversation: conversation,
                    getCategoryCounts: function () {
                        return loader.getKnowledgeCategoryCounts();
                    },
                    getLastAnswer: function () {
                        return conversation.getLastAnswer();
                    },
                    resetCategory: function () {
                        conversation._selectedCategory = null;
                    }
                });
            },
            getCategoryCounts: function () {
                return loader.getKnowledgeCategoryCounts();
            },
            onCategorySelect: function (key) {
                conversation._selectedCategory = key || null;
                if (options.debug && typeof console !== 'undefined') {
                    console.debug('[IONLOOP Assistant] category selected:', key);
                }
                // Render a compact overview for the selected category using real
                // indexed content — shared by the Knowledge sidebar and /topics.
                if (key && ui && ui.showCategoryOverview) {
                    var cat = categoriesMod.CATEGORIES.find(function (c) { return c.key === key; });
                    if (cat) {
                        var docs = search.getDocuments().filter(function (d) { return d.category === key; });
                        var suggestions = docs.slice(0, 4).map(function (d) {
                            return d.title.charAt(0).toUpperCase() + d.title.slice(1);
                        });
                        ui.showCategoryOverview({
                            status: 'answered',
                            type: 'system',
                            title: cat.label + ' selected',
                            intro: 'Ask about ' + cat.label.toLowerCase() + ':',
                            paragraphs: [],
                            bullets: suggestions,
                            actions: [],
                            sources: [],
                            followUps: [],
                            confidence: 1
                        });
                    }
                }
            }
        });

        var instance = {
            ask: function (question, askOptions) {
                return askAssistant(question, askOptions || {}, {
                    conversation: conversation,
                    graph: graph,
                    index: index,
                    debug: !!(options.debug || (askOptions && askOptions.debug))
                });
            },
            getContext: function () {
                return conversation.buildContext();
            },
            getExchanges: function () {
                return conversation.getExchanges();
            },
            clear: function () {
                conversation.clear();
                return { status: 'reset' };
            },
            renderAnswer: renderer.renderAnswer,
            getPrimaryEntity: function () {
                return conversation.getPrimaryEntity();
            },
            graph: graph,
            ui: ui
        };

        if (typeof window !== 'undefined') {
            window.IonloopAssistant = instance;
        }
        return instance;
    });
}

/**
 * Full single-turn pipeline:
 * resolve context → search → compose → record exchange.
 */
function askAssistant(question, options, state) {
    var started = Date.now();
    var rawQuestion = String(question || '').trim();

    var resolution = resolver.resolveQuestion(rawQuestion, state.conversation, state.graph);
    var query = resolution.resolvedQuery || rawQuestion;

    // -- Reset -------------------------------------------------------------
    if (resolution.status === 'reset') {
        state.conversation.clear();
        return {
            response: {
                status: 'answered',
                type: 'reset',
                intro: 'OK — starting a new conversation.',
                title: '',
                paragraphs: [],
                bullets: [],
                actions: [],
                sources: [],
                followUps: [],
                confidence: 1
            },
            debug: { resolution: resolution, elapsedMs: Date.now() - started }
        };
    }

    // -- Contextual action ("Open it") -------------------------------------
    if (resolution.status === 'action') {
        return {
            response: {
                status: 'answered',
                type: 'navigation',
                intro: 'Opening that for you:',
                title: resolution.action.label || '',
                actions: [resolution.action],
                paragraphs: [],
                bullets: [],
                sources: [],
                followUps: [],
                confidence: 0.9
            },
            debug: { resolution: resolution, elapsedMs: Date.now() - started }
        };
    }

    // -- Refinement ("explain simpler" / "more detail") --------------------
    if (resolution.status === 'refinement') {
        var lastRetrieval = state.conversation.getLastRetrieval();
        var lastAnswer = state.conversation.getLastAnswer();
        if (lastRetrieval && lastAnswer) {
            var refined = composer.composeAnswer(lastRetrieval.query || rawQuestion, lastRetrieval, {
                verbosity: resolution.mode,
                primaryEntity: lastAnswer.primaryEntity || lastAnswer.title || '',
                type: lastAnswer.type
            });
            refined.reusedRetrieval = true;
            return {
                response: refined,
                debug: { resolution: resolution, reuse: true, elapsedMs: Date.now() - started }
            };
        }
        // No prior exchange to refine.
        resolution.status = 'standalone';
    }

    // -- Clarification -----------------------------------------------------
    if (resolution.status === 'clarify') {
        var candidates = resolution.clarificationCandidates || state.conversation.getRecentTopics().slice(0, 3);
        return {
            response: {
                status: 'ambiguous',
                type: 'ambiguous',
                intro: 'I found several possible topics. Did you mean:',
                title: '',
                bullets: candidates,
                suggestions: candidates,
                paragraphs: [],
                actions: [],
                sources: [],
                followUps: [],
                confidence: resolution.confidence || 0.3
            },
            debug: { resolution: resolution, elapsedMs: Date.now() - started }
        };
    }

    // -- Search with context ----------------------------------------------
    var searchOptions = { limit: options.limit || 6 };
    if (resolution.context && resolution.context.active) {
        searchOptions.context = resolution.context;
    }
    if (resolution.categoryContinuation) {
        searchOptions.category = resolution.categoryContinuation;
    }
    // Selected knowledge category acts as a moderate ranking hint (not a
    // strict filter) so a clearly different topic can still surface.
    var selectedCategory = (state.conversation && state.conversation._selectedCategory) || options.category;
    if (selectedCategory && !searchOptions.category) {
        searchOptions.category = selectedCategory;
    }

    var retrieval = search.searchKnowledge(query, searchOptions);
    var answer = composer.composeAnswer(rawQuestion, retrieval, {
        primaryEntity: options.primaryEntity || ''
    });
    if (resolution.topicSwitch) {
        answer.topicSwitch = true;
    }

    // -- Primary entity tracking ------------------------------------------
    var primaryEntity = answer.title || (retrieval.results[0] ? retrieval.results[0].title : '') || '';
    var entities = retrieval.results.slice(0, 5).map(function (r) { return r.title; });
    var documentIds = [];
    var sectionIds = [];
    retrieval.results.forEach(function (r) {
        if (documentIds.indexOf(r.documentId) === -1) { documentIds.push(r.documentId); }
        sectionIds.push(r.sectionId);
    });

    state.conversation.addExchange({
        role: 'user',
        query: rawQuestion,
        normalizedQuery: retrieval.normalizedQuery,
        answerType: answer.type,
        primaryEntity: primaryEntity,
        category: retrieval.results[0] ? retrieval.results[0].category : null,
        documentIds: documentIds,
        sectionIds: sectionIds,
        followUpTopics: answer.followUps || [],
        entities: entities,
        comparisonEntities: retrieval.comparisonEntities || [],
        retrieval: retrieval,
        answer: answer
    });
    answer.exchangeId = state.conversation.getLast().id;
    answer.primaryEntity = primaryEntity;

    var response = { response: answer, elapsedMs: Date.now() - started };
    if (state.debug || options.debug) {
        response.debug = {
            resolution: resolution,
            resolvedQuery: query,
            retrieval: retrieval,
            context: state.conversation.buildContext()
        };
    }
    return response;
}

module.exports = {
    initIonloopAssistant: initIonloopAssistant,
    askAssistant: askAssistant,
    conversation: conversationMod,
    resolver: resolver,
    renderer: renderer
};

// Auto-boot the assistant when this module is bundled into a page.
// Runs once on DOM ready; initIonloopAssistant() fails gracefully if the
// knowledge index cannot be loaded.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    function bootAssistant() {
        initIonloopAssistant();
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootAssistant);
    } else {
        bootAssistant();
    }
}
